import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Loader2, UserPlus, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { errMsg } from "@/lib/api";
import { useMediaQuery } from "@/hooks/use-media-query";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { UserAvatar } from "@/components/common";
import { brukereApi, firstName } from "@/lib/brukere";
import { Credentials } from "@/components/brukere/Credentials";
import { DeptSelect, Field, FlagList, RolePicker, fieldCls, primaryBtn, quietBtn } from "@/components/brukere/Fields";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/;
const blank = (m) => ({ name: m?.name || "", email: "", title: m?.title || "", department: m?.department || null, role: null, flags: {} });
const initialsOf = (name) => (name || "").trim().split(/[\s-]+/).filter(Boolean).slice(0, 2).map((p) => p[0]).join("").toUpperCase();

/**
 * Create an account – or give an existing team member a login (same person, same id, assignments follow).
 * Result view shows the one-time temporary password; closing it is the only way out, so it is never lost by accident.
 */
export function NewUserSheet({ open, member, data, onClose, onCreated }) {
  const mobile = !useMediaQuery("(min-width: 640px)");
  const [f, setF] = useState(blank(member));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [touched, setTouched] = useState({});
  const [more, setMore] = useState(false);
  const [done, setDone] = useState(null);
  const nameRef = useRef(null);
  const emailRef = useRef(null);

  useEffect(() => {
    if (open) { setF(blank(member)); setErr(null); setTouched({}); setMore(false); setDone(null); setBusy(false); }
  }, [open, member]);

  const emailTaken = useMemo(() => {
    const e = f.email.trim().toLowerCase();
    return e && (data?.users || []).some((u) => u.email === e);
  }, [f.email, data]);
  const errors = {
    name: f.name.trim().length < 2 ? "Skriv navnet" : null,
    email: !EMAIL_RE.test(f.email.trim()) ? "Skriv en gyldig e-postadresse" : emailTaken ? "E-postadressen er allerede i bruk" : null,
    role: !f.role ? "Velg en rolle" : null,
  };
  const valid = !errors.name && !errors.email && !errors.role;
  const set = (k) => (v) => setF((cur) => ({ ...cur, [k]: v }));
  const anyFlag = Object.values(f.flags).some(Boolean);

  const submit = async (e) => {
    e?.preventDefault();
    setTouched({ name: true, email: true, role: true });
    if (!valid || busy) return;
    setBusy(true); setErr(null);
    try {
      const body = { name: f.name.trim(), email: f.email.trim().toLowerCase(), role: f.role, title: f.title.trim() || null, department: f.department || "", flags: f.flags };
      if (member) body.from_member_id = member.id;
      const r = await brukereApi.create(body);
      setDone(r.data);
      onCreated?.(r.data.user);
    } catch (e2) {
      setErr(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };

  const again = () => { setF(blank(null)); setTouched({}); setMore(false); setDone(null); setErr(null); requestAnimationFrame(() => nameRef.current?.focus()); };
  const u = done?.user;

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side={mobile ? "bottom" : "right"} hideClose aria-describedby={undefined}
        onOpenAutoFocus={(e) => { e.preventDefault(); requestAnimationFrame(() => (member ? emailRef.current : nameRef.current)?.focus()); }}
        className={cn("flex flex-col gap-0 p-0", mobile ? "h-[94dvh] rounded-t-[20px]" : "w-[min(100vw,540px)] sm:max-w-[540px]")} data-testid="new-user-sheet">
        <SheetTitle className="sr-only">{member ? `Gi innlogging til ${member.name}` : "Ny bruker"}</SheetTitle>
        <div className="flex items-center gap-3 px-6 pb-3 pt-5">
          <div className="min-w-0 flex-1">
            <div className="text-[18px] font-semibold tracking-[-0.015em]" data-testid="new-user-title">{done ? `${firstName(u?.name)} kan logge inn` : member ? `Gi innlogging til ${firstName(member.name)}` : "Ny bruker"}</div>
            <div className="mt-0.5 text-[13px] text-muted-foreground">{done ? "Del innloggingen – passordet vises bare én gang." : member ? "Samme person som i teamlisten. Tildelte poster følger med." : "Brukeren får et midlertidig passord og velger sitt eget ved første innlogging."}</div>
          </div>
          <button type="button" onClick={onClose} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-black/[0.05] hover:text-foreground" aria-label="Lukk" data-testid="new-user-close"><X className="h-4 w-4" /></button>
        </div>

        {done ? (
          <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6 scrollbar-thin" data-testid="new-user-done">
            <div className="flex items-center gap-3 py-3">
              <UserAvatar name={u.name} initials={u.initials} color={u.color} size="lg" className="h-11 w-11 text-[14px]" />
              <div className="min-w-0">
                <div className="truncate text-[15px] font-semibold">{u.name}</div>
                <div className="truncate text-[13px] text-muted-foreground">{[u.role_label, u.title, u.dept_label].filter(Boolean).join(" · ")}</div>
              </div>
            </div>
            <Credentials name={u.name} email={u.email} password={done.temp_password} testId="new-user-credentials" />
          </div>
        ) : (
          <form onSubmit={submit} className="min-h-0 flex-1 overflow-y-auto px-6 pb-6 scrollbar-thin" noValidate data-testid="new-user-form">
            <div className="flex items-center gap-3 pb-4 pt-1">
              <UserAvatar name={f.name || "?"} initials={initialsOf(f.name) || "?"} color={member?.color || "hsl(207 30% 70%)"} size="lg" className="h-11 w-11 text-[14px] transition-colors" />
              <div className="min-w-0 text-[13px] text-muted-foreground">{f.name.trim() ? <><span className="font-medium text-foreground">{f.name.trim()}</span>{f.role ? ` · ${(data?.roles || []).find((r) => r.key === f.role)?.label}` : ""}</> : "Navn og e-post er nok for å komme i gang."}</div>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Navn" htmlFor="nu-name" error={touched.name && errors.name}>
                <input ref={nameRef} id="nu-name" value={f.name} onChange={(e) => set("name")(e.target.value)} onBlur={() => setTouched((t) => ({ ...t, name: true }))} placeholder="Fornavn Etternavn" autoComplete="off" aria-invalid={Boolean(touched.name && errors.name)} className={fieldCls} data-testid="new-user-name" />
              </Field>
              <Field label="E-post" htmlFor="nu-email" error={touched.email && errors.email}>
                <input ref={emailRef} id="nu-email" type="email" value={f.email} onChange={(e) => set("email")(e.target.value)} onBlur={() => setTouched((t) => ({ ...t, email: true }))} placeholder="navn@los-marine.no" autoComplete="off" aria-invalid={Boolean(touched.email && errors.email)} className={fieldCls} data-testid="new-user-email" />
              </Field>
              <Field label="Tittel" htmlFor="nu-title" hint="Valgfritt – vises i team og på saker.">
                <input id="nu-title" value={f.title} onChange={(e) => set("title")(e.target.value)} placeholder="F.eks. Prosjektleder" autoComplete="off" className={fieldCls} data-testid="new-user-title-input" />
              </Field>
              <Field label="Avdeling" htmlFor="nu-dept">
                <DeptSelect id="nu-dept" value={f.department} onChange={set("department")} departments={data?.departments} testId="new-user-dept" />
              </Field>
            </div>

            <div className="mt-6">
              <div className="mb-2 flex items-baseline justify-between"><span className="text-[13px] font-semibold">Rolle</span>{touched.role && errors.role ? <span className="text-[12px] text-[hsl(var(--v-blocked))]" role="alert">{errors.role}</span> : null}</div>
              <RolePicker roles={data?.roles} value={f.role} onChange={set("role")} note={data?.role_note} testId="new-user-role" />
            </div>

            <div className="mt-6">
              <button type="button" onClick={() => setMore((v) => !v)} aria-expanded={more} className="flex w-full items-center justify-between gap-2 text-left text-[13px] font-semibold" data-testid="new-user-flags-toggle">
                <span>Adminrettigheter <span className="font-normal text-muted-foreground">{anyFlag ? `· ${Object.values(f.flags).filter(Boolean).length} valgt` : "· ingen (vanlig)"}</span></span>
                <ChevronDown className={cn("h-4 w-4 text-muted-foreground transition-transform duration-200", more && "rotate-180")} />
              </button>
              {more ? <div className="mt-2.5 fade-up"><FlagList flags={data?.flags} value={f.flags} onChange={set("flags")} testId="new-user-flags" /></div> : null}
            </div>

            {err ? <p className="mt-4 rounded-[10px] bg-[hsl(var(--v-blocked)/0.07)] px-3 py-2 text-[13px] text-[hsl(var(--v-blocked))]" role="alert" data-testid="new-user-error">{err}</p> : null}
          </form>
        )}

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-black/[0.06] px-6 py-3.5">
          {done ? (
            <>
              {!member ? <button type="button" onClick={again} className={quietBtn} data-testid="new-user-another"><UserPlus className="h-4 w-4" /> Legg til en til</button> : null}
              <button type="button" onClick={onClose} className={primaryBtn} data-testid="new-user-finish">Ferdig</button>
            </>
          ) : (
            <>
              <button type="button" onClick={onClose} className={quietBtn} data-testid="new-user-cancel">Avbryt</button>
              <button type="button" onClick={submit} disabled={busy || (Object.keys(touched).length > 0 && !valid)} className={primaryBtn} data-testid="new-user-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}{member ? "Gi innlogging" : "Opprett bruker"}</button>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
